<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';
import { syncRefs, useSupported, useVModel } from '@vueuse/core';
import { captureException } from '@sentry/vue';

import { useDelayedLoading } from '~~core';
import { SecureEnvironment } from '~~base-ui';

import { PDF_SCALES } from '../../constants/pdf';
import PDFVisualizer from '../PDFVisualizer/PDFVisualizer.vue';
import PDFVisualizerSkeleton from '../PDFVisualizerSkeleton/PDFVisualizerSkeleton.vue';
import PDFVisualizerError from '../PDFVisualizerError/PDFVisualizerError.vue';
import DocumentPreviewZoom from '../DocumentPreviewZoom/DocumentPreviewZoom.vue';

type ZoomAPI = {
  increaseZoom(): void;
  decreaseZoom(): void;
  resetZoom(): void;
};

export type DocumentPreviewEmits = {
  (e: 'error', err: Error): void;
  (e: 'pages-load', pagesCount?: number): void;
  (e: 'update:legacy', value: boolean): void;
};

export type DocumentPreviewProps = {
  documentPdfUrl: string;
  documentKey: string;
  loaderClass?: string;
  errorClass?: string;
  initialScale?: string;
  containerClass?: string;
  legacy?: boolean;
  relative?: boolean;
  customRetry?: () => void;
};

function useBrowserSupport() {
  const isResizeObserveSupported = useSupported(() => 'ResizeObserver' in window);
  const isCanvasGetTransformSupported = useSupported(() => CanvasRenderingContext2D && 'getTransform' in CanvasRenderingContext2D.prototype);
  const isAbortControllerSupported = useSupported(() => 'AbortController' in window);

  const hasMissingFeatures = computed(
    () => !(
      isCanvasGetTransformSupported.value
      && isResizeObserveSupported.value
      && isAbortControllerSupported.value
    ),
  );

  return {
    hasMissingFeatures,
  };
}

const PDFLegacyVisualizer = defineAsyncComponent(() => import('../PDFLegacyVisualizer/PDFLegacyVisualizer.vue'));
const HUMAN_PERCEPTION_MS = 200;
const isLegacyBuild = import.meta.env.LEGACY;

const emit = defineEmits<DocumentPreviewEmits>();
const props = withDefaults(defineProps<DocumentPreviewProps>(), {
  loaderClass: '',
  errorClass: '',
  initialScale: PDF_SCALES.AUTO,
  containerClass: '',
  relative: true,
});

const isLoadingPDFJs = ref(true);
const showLegacyPreview = ref(false);
const pdfVisualizer = ref<ZoomAPI>();

const { hasMissingFeatures } = useBrowserSupport();
const { isLoading } = useDelayedLoading(isLoadingPDFJs, HUMAN_PERCEPTION_MS);

const isLegacy = computed(() => props.legacy || isLegacyBuild || hasMissingFeatures.value);
const shouldShowLegacy = computed(() => isLegacy.value || showLegacyPreview.value);

syncRefs(isLegacy, useVModel(props, 'legacy', emit));

function handlePagesLoad(pagesCount?: number) {
  isLoadingPDFJs.value = false;
  emit('pages-load', pagesCount);
}

function handleError(e: unknown) {
  const err = e instanceof Error ? e : new Error(`${e}`, { cause: e });

  emit('error', err);
  isLoadingPDFJs.value = false;
  captureException(err, {
    tags: { pdfJs: true },
  });
}

function legacyRetry() {
  isLoadingPDFJs.value = true;
  showLegacyPreview.value = true;
}

function handleRetry() {
  if (typeof props.customRetry === 'function') return props.customRetry();
  legacyRetry();
}
</script>

<template>
  <div :class="{ [$style.preview]: relative }">
    <DocumentPreviewZoom
      v-if="pdfVisualizer"
      :class="$style.zoomControls"
      @zoom-reset="pdfVisualizer.resetZoom()"
      @zoom-in="pdfVisualizer.increaseZoom()"
      @zoom-out="pdfVisualizer.decreaseZoom()"
    />

    <div
      v-if="isLoading"
      :class="loaderClass"
    >
      <PDFVisualizerSkeleton />
    </div>

    <div v-if="shouldShowLegacy">
      <PDFLegacyVisualizer
        v-show="!isLoading"
        ref="pdfVisualizer"
        data-testid="documentPreviewLegacyVisualizer"
        :document-key="documentKey"
        v-bind="$attrs"
        @pages-load="handlePagesLoad"
      />
    </div>

    <NuxtErrorBoundary
      v-else
      @error="handleError"
    >
      <template #default>
        <PDFVisualizer
          ref="pdfVisualizer"
          :class="{ [$style.softHidden]: isLoading }"
          :document-pdf-url="documentPdfUrl"
          :container-class="containerClass"
          :initial-scale="initialScale"
          :relative="relative"
          v-bind="$attrs"
          @pages-load="handlePagesLoad"
          @document-load="handlePagesLoad"
        />
      </template>

      <template #error>
        <div :class="errorClass">
          <PDFVisualizerError @retry="handleRetry" />
          <SecureEnvironment />
        </div>
      </template>
    </NuxtErrorBoundary>
  </div>
</template>

<style module lang="scss">
.preview {
  position: relative;
}

.softHidden {
  overflow: hidden;
  height: 0;
}

.zoomControls {
  position: absolute;
  z-index: var(--zindex-sticky);
  top: var(--space-regular);
  left: 0;
}
</style>
